import {
    Badge,
    Box,
    Collapse,
    Flex,
    HStack,
    Icon,
    Table,
    TableContainer,
    Tbody,
    Td,
    Text,
    Th,
    Thead,
    Tr,
    useDisclosure,
    VStack,
} from '@chakra-ui/react';
import { TbChevronRight, TbFileDescription, TbPlus, TbMinus, TbArrowRight } from 'react-icons/tb';
import PartType from '../PartType';
import { formatPrice } from '../../functions';

/**
 * EMS export-history info-graphic for Ask Carl. Mirrors what the order page's
 * Export History drawer / EmsExportHistoryTimeline show, but as a table of
 * exports with a collapsible per-export diff of parts added vs. the previous
 * estimate.
 */

interface ExportPart {
    lineNumber?: number;
    partNumber?: string;
    partName?: string;
    type?: string;
    quantity?: number;
    listPrice?: number;
}

interface ChangedPart {
    before: ExportPart;
    after: ExportPart;
    typeChanged?: boolean;
    priceChanged?: boolean;
}

export interface EmsExportEntry {
    emsId: string;
    exportNumber: number;
    timestamp: string;
    totalParts: number;
    isInitial: boolean;
    addedCount: number;
    removedCount: number;
    changedCount?: number;
    addedParts?: ExportPart[];
    removedParts?: ExportPart[];
    changedParts?: ChangedPart[];
    parts?: ExportPart[];
}

export interface EmsExportHistoryData {
    orderId?: string;
    order?: string;
    exports: EmsExportEntry[];
}

const formatWhen = (timestamp: string) => {
    const date = new Date(timestamp);
    return `${date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })} · ${date.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' })}`;
};

const priceText = (value?: number) => (typeof value === 'number' ? formatPrice(value) : '—');

const ExportRow = ({ entry, isLast }: { entry: EmsExportEntry; isLast: boolean; }) => {
    const { isOpen, onToggle } = useDisclosure();
    const added = entry.addedParts ?? [];
    const removed = entry.removedParts ?? [];
    const changed = entry.changedParts ?? [];
    const hasDiff = added.length > 0 || removed.length > 0 || changed.length > 0;

    return (
        <Box borderBottom={isLast ? undefined : '1px solid'} borderColor="gray.200">
            <Flex
                as="button"
                w="100%"
                textAlign="left"
                align="center"
                gap={3}
                px={3}
                py={2.5}
                _hover={{ bg: 'gray.50' }}
                transition="background 0.15s"
                onClick={onToggle}
            >
                <Icon
                    as={TbChevronRight as React.ElementType}
                    boxSize={4}
                    color="gray.400"
                    transform={isOpen ? 'rotate(90deg)' : undefined}
                    transition="transform 0.15s"
                    flexShrink={0}
                />
                <Flex align="center" justify="center" w="28px" h="28px" borderRadius="full" bg="blue.50" flexShrink={0}>
                    <Icon as={TbFileDescription as React.ElementType} boxSize={3.5} color="#0071E3" />
                </Flex>
                <Box minW={0} flex={1}>
                    <Text fontSize="sm" fontWeight="semibold" color="gray.800">
                        Export {entry.exportNumber}
                    </Text>
                    <Text fontSize="xs" color="gray.500">
                        {formatWhen(entry.timestamp)}
                    </Text>
                </Box>
                <HStack spacing={1.5} flexShrink={0}>
                    <Badge bg="gray.100" color="gray.600" fontSize="10px">{entry.totalParts} parts</Badge>
                    {entry.isInitial ? (
                        <Badge colorScheme="blue" variant="subtle" fontSize="10px">INITIAL</Badge>
                    ) : (
                        <>
                            {entry.addedCount > 0 && (
                                <Badge colorScheme="green" variant="subtle" fontSize="10px">+{entry.addedCount}</Badge>
                            )}
                            {entry.removedCount > 0 && (
                                <Badge colorScheme="red" variant="subtle" fontSize="10px">−{entry.removedCount}</Badge>
                            )}
                            {(entry.changedCount ?? 0) > 0 && (
                                <Badge colorScheme="purple" variant="subtle" fontSize="10px">~{entry.changedCount}</Badge>
                            )}
                            {entry.addedCount === 0 && entry.removedCount === 0 && (entry.changedCount ?? 0) === 0 && (
                                <Badge bg="gray.100" color="gray.500" fontSize="10px">NO CHANGE</Badge>
                            )}
                        </>
                    )}
                </HStack>
            </Flex>
            <Collapse in={isOpen} animateOpacity>
                <Box px={3} pb={3} pt={1}>
                    {entry.isInitial ? (
                        <Text fontSize="xs" color="gray.500" mb={2}>
                            First estimate exported — {entry.totalParts} parts.
                        </Text>
                    ) : !hasDiff ? (
                        <Text fontSize="xs" color="gray.500">No part changes from the previous export.</Text>
                    ) : null}

                    {/* Replacements: same part, before → after (e.g. RCY → OEM, price change) */}
                    {changed.length > 0 && (
                        <Box mb={added.length || removed.length ? 3 : 0}>
                            <HStack spacing={2} mb={1.5}>
                                <Icon as={TbArrowRight as React.ElementType} boxSize={3} color="purple.500" />
                                <Text fontSize="xs" color="gray.600">
                                    {changed.length} replacement{changed.length === 1 ? '' : 's'} (same part, changed)
                                </Text>
                            </HStack>
                            <VStack align="stretch" spacing={1.5}>
                                {changed.map((entryChange, index) => (
                                    <Box
                                        key={`chg-${entryChange.after.partNumber}-${index}`}
                                        border="1px solid"
                                        borderColor="purple.200"
                                        borderRadius="lg"
                                        bg="purple.50"
                                        px={3}
                                        py={2}
                                    >
                                        <Text fontSize="xs" color="gray.800" fontWeight="medium" mb={1}>
                                            {entryChange.after.partName || entryChange.before.partName || 'Part'}
                                            {entryChange.after.partNumber && (
                                                <Text as="span" color="gray.500" fontFamily="mono" fontWeight="normal"> · {entryChange.after.partNumber}</Text>
                                            )}
                                            {entryChange.after.lineNumber != null && (
                                                <Text as="span" color="gray.400" fontWeight="normal"> · line {entryChange.after.lineNumber}</Text>
                                            )}
                                        </Text>
                                        <HStack spacing={2} align="center" flexWrap="wrap">
                                            {/* before */}
                                            <HStack spacing={1.5} opacity={0.75}>
                                                {entryChange.before.type ? <PartType partType={entryChange.before.type} /> : <Text fontSize="xs" color="gray.500">—</Text>}
                                                <Text fontSize="xs" color="gray.500">{priceText(entryChange.before.listPrice)}</Text>
                                            </HStack>
                                            <Icon as={TbArrowRight as React.ElementType} boxSize={3.5} color="purple.500" />
                                            {/* after */}
                                            <HStack spacing={1.5}>
                                                {entryChange.after.type ? <PartType partType={entryChange.after.type} /> : <Text fontSize="xs" color="gray.800">—</Text>}
                                                <Text fontSize="xs" color="gray.800" fontWeight="medium">{priceText(entryChange.after.listPrice)}</Text>
                                            </HStack>
                                            {entryChange.typeChanged && (
                                                <Badge colorScheme="purple" variant="subtle" fontSize="9px">TYPE</Badge>
                                            )}
                                            {entryChange.priceChanged && (
                                                <Badge colorScheme="orange" variant="subtle" fontSize="9px">PRICE</Badge>
                                            )}
                                        </HStack>
                                    </Box>
                                ))}
                            </VStack>
                        </Box>
                    )}

                    {/* Added / initial parts table */}
                    {(entry.isInitial ? entry.parts ?? [] : added).length > 0 && (
                        <>
                            {!entry.isInitial && (
                                <HStack spacing={2} mb={1.5}>
                                    <Icon as={TbPlus as React.ElementType} boxSize={3} color="green.500" />
                                    <Text fontSize="xs" color="gray.600">
                                        {added.length} new part{added.length === 1 ? '' : 's'} added
                                    </Text>
                                </HStack>
                            )}
                            <TableContainer border="1px solid" borderColor="gray.200" borderRadius="lg">
                                <Table size="sm" variant="simple">
                                    <Thead>
                                        <Tr>
                                            <Th fontSize="10px" color="gray.500" borderColor="gray.200">Line</Th>
                                            <Th fontSize="10px" color="gray.500" borderColor="gray.200">Type</Th>
                                            <Th fontSize="10px" color="gray.500" borderColor="gray.200">Description</Th>
                                            <Th fontSize="10px" color="gray.500" borderColor="gray.200">OE Part #</Th>
                                            <Th fontSize="10px" color="gray.500" borderColor="gray.200" isNumeric>Qty</Th>
                                            <Th fontSize="10px" color="gray.500" borderColor="gray.200" isNumeric>Price</Th>
                                        </Tr>
                                    </Thead>
                                    <Tbody>
                                        {(entry.isInitial ? entry.parts ?? [] : added).map((part, index) => (
                                            <Tr key={`${part.partNumber}-${part.lineNumber}-${index}`}>
                                                <Td fontSize="xs" color="gray.700" borderColor="gray.200">{part.lineNumber ?? '—'}</Td>
                                                <Td fontSize="xs" color="gray.700" borderColor="gray.200">{part.type ? <PartType partType={part.type} /> : '—'}</Td>
                                                <Td fontSize="xs" color="gray.700" borderColor="gray.200">{part.partName || '—'}</Td>
                                                <Td fontSize="xs" color="gray.700" borderColor="gray.200" fontFamily="mono">{part.partNumber || '—'}</Td>
                                                <Td fontSize="xs" color="gray.700" borderColor="gray.200" isNumeric>{part.quantity ?? '—'}</Td>
                                                <Td fontSize="xs" color="gray.700" borderColor="gray.200" isNumeric>{priceText(part.listPrice)}</Td>
                                            </Tr>
                                        ))}
                                    </Tbody>
                                </Table>
                            </TableContainer>
                        </>
                    )}

                    {/* Removed parts */}
                    {removed.length > 0 && (
                        <Box mt={added.length || entry.isInitial ? 3 : 0}>
                            <HStack spacing={2} mb={1.5}>
                                <Icon as={TbMinus as React.ElementType} boxSize={3} color="red.500" />
                                <Text fontSize="xs" color="gray.600">
                                    {removed.length} part{removed.length === 1 ? '' : 's'} removed
                                </Text>
                            </HStack>
                            <VStack align="stretch" spacing={1}>
                                {removed.map((part, index) => (
                                    <HStack key={`rm-${part.partNumber}-${index}`} spacing={2} opacity={0.7}>
                                        {part.type ? <PartType partType={part.type} /> : null}
                                        <Text fontSize="xs" color="gray.600" textDecoration="line-through">
                                            {part.partName || part.partNumber || 'Part'}
                                        </Text>
                                        {part.partNumber && (
                                            <Text fontSize="xs" color="gray.400" fontFamily="mono">{part.partNumber}</Text>
                                        )}
                                    </HStack>
                                ))}
                            </VStack>
                        </Box>
                    )}
                </Box>
            </Collapse>
        </Box>
    );
};

const EmsExportHistoryCard = ({ history }: { history: EmsExportHistoryData; }) => {
    const exports = [...(history.exports ?? [])].sort((a, b) => b.exportNumber - a.exportNumber);
    if (!exports.length) return null;

    return (
        <Box border="1px solid" borderColor="gray.200" borderRadius="xl" bg="white" boxShadow="none" overflow="hidden">
            <HStack px={3} py={2.5} justify="space-between" borderBottom="1px solid" borderColor="gray.200">
                <Text fontSize="sm" fontWeight="semibold" color="gray.800">EMS Export History</Text>
                <Badge colorScheme="blue" variant="subtle" fontSize="10px">
                    {exports.length} export{exports.length === 1 ? '' : 's'}
                </Badge>
            </HStack>
            <VStack align="stretch" spacing={0}>
                {exports.map((entry, index) => (
                    <ExportRow key={entry.emsId} entry={entry} isLast={index === exports.length - 1} />
                ))}
            </VStack>
        </Box>
    );
};

export default EmsExportHistoryCard;
